import { useState, useEffect, useCallback, useMemo } from 'react';
import { adminAPI } from '@/services/api';
import { MSAService } from '@/types';
import { HealthStatus } from '@/types/health';

export const useServices = (statusFilter?: string) => {
  const [services, setServices] = useState<MSAService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [healthStatuses, setHealthStatuses] = useState<Record<number, HealthStatus>>({});
  const [searchQuery, setSearchQuery] = useState('');

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminAPI.getServices(statusFilter);
      setServices(response.services || []);
      
      // APPROVED 또는 PENDING 상태의 서비스들의 상태 확인
      const targetServices = response.services.filter((s: MSAService) => s.status === 'approved' || s.status === 'pending');
      if (targetServices.length > 0) {
        const serviceSlugs = targetServices.map((s: MSAService) => s.service_slug);
        try {
          const healthResponse = await adminAPI.batchHealthCheck(serviceSlugs);
          const statusMap: Record<number, HealthStatus> = {};
          // service_slug를 service_id로 매핑
          Object.keys(healthResponse.results).forEach((slug) => {
            const service = targetServices.find((s: MSAService) => s.service_slug === slug);
            if (service) {
              statusMap[service.id] = healthResponse.results[slug].status;
            }
          });
          setHealthStatuses(statusMap);
        } catch (healthError) {
          console.error('Health check failed:', healthError);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch services:', err);
      setError(err.response?.data?.detail || '서비스 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const filteredServices = useMemo(() => {
    if (!searchQuery.trim()) return services;
    const query = searchQuery.toLowerCase();
    return services.filter(service =>
      service.service_name.toLowerCase().includes(query) ||
      service.service_description?.toLowerCase().includes(query) ||
      service.category?.toLowerCase().includes(query) ||
      service.contact_name?.toLowerCase().includes(query)
    );
  }, [services, searchQuery]);

  return {
    services,
    filteredServices,
    loading,
    error,
    setError,
    healthStatuses,
    setHealthStatuses,
    searchQuery,
    setSearchQuery,
    fetchServices,
  };
};

